import { Box, Button, Checkbox, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import ReactLoading from 'react-loading';
import { generateClient } from 'aws-amplify/api';
import { Schema } from '../../../amplify/data/resource';
import { SFNClient, StartExecutionCommand } from '@aws-sdk/client-sfn';
import awsConfig from '../../amplify_outputs.json';
import { formatFileDate } from '../../utils/format/format';
import { useNavigate } from 'react-router-dom';
import SendButton from './SendButton';
import { useAuth } from '../../utils/context/useAuthContext';

const apiClient = generateClient<Schema>();

const STEP_FUNCTION_ARN = awsConfig.custom.processFileStepFunctionArn;
const S3_BUCKET_NAME = awsConfig.storage.bucket_name;

export const EmptyState = () => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);

  const { user } = useAuth();

  const userId = user?.userId;

  const isUploadFileOpen = true;

  const [file, setFile] = useState<File | null>(null);
  const [isReplace, setIsReplace] = useState(false);
  const [replace, setReplace] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const uploadFileRef = useRef<HTMLDivElement | null>(null);

  const handleCloseUploadFile = () => {};

  const handleBoxClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsReplace(false);
    setReplace(false);
    const uploadedFile = event.target.files?.[0];
    if (uploadedFile) {
      setFile(uploadedFile);
      event.target.value = '';
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files?.[0];
    if (droppedFile) {
      setFile(droppedFile);
    }
  };

  const startStepFunction = async (fileId: string, replace: boolean) => {
    try {
      const triggerProcessFile = await apiClient.queries.triggerProcessFile({
        fileId,
        replace,
        arn: STEP_FUNCTION_ARN,
      });

      console.log('Step Function started:', triggerProcessFile);

      handleCloseUploadFile();
    } catch (error) {
      console.error('Failed to start Step Function:', error);
    }
  };

  // Helper function to convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix to get pure base64
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const handleNextStep = async () => {
    if (!file || !userId) {
      console.error('No file selected or user ID is missing');
      return;
    }

    setIsLoading(true);

    const reader = new FileReader();

    reader.onload = async (e) => {
      const text = e.target?.result;
      if (typeof text === 'string') {
        try {
          const executionDateMatch = text.match(
            /<TAARICH-BITZUA>(.*?)<\/TAARICH-BITZUA>/,
          );
          const rawExecutionDate = executionDateMatch
            ? executionDateMatch[1]
            : '';
          const fileExecutionDate = formatFileDate(rawExecutionDate);

          if (!fileExecutionDate) {
            console.error('Failed to extract execution date from file.');
            setIsLoading(false);
            return;
          }

          console.log('Execution Date from file:', fileExecutionDate);

          const fileListResponse = await apiClient.models.File.listFileByDate(
            { date: fileExecutionDate },
            { filter: { userId: { eq: userId } } },
          );

          const existingFile = fileListResponse?.data?.[0];

          if (!existingFile) {
            const createdFile = await apiClient.models.File.create({
              date: fileExecutionDate,
              status: 'PROCESSING',
              userId: userId,
              fileName: file.name,
              viewed: true
            });

            console.log('Created file:', createdFile);

            const fileId = createdFile?.data?.id;
            if (!fileId) {
              console.error('Failed to create file record.');
              setIsLoading(false);
              return;
            }

            console.log('Uploading file to S3...');
            try {
              // Convert file to base64
              const base64File = await fileToBase64(file);
              
              const uploadResponse = await apiClient.queries.uploadFile({
                file: base64File,
                path: `files/${fileId}`,
                bucketName: S3_BUCKET_NAME,
              });

              console.log('File successfully uploaded to S3 with ID:', fileId);
              console.log('Upload response:', uploadResponse);

              await startStepFunction(fileId, false);
              navigate(`/file-overview?fileId=${fileId}`);
            } catch (uploadError) {
              console.error('Failed to upload file to S3:', uploadError);
              setIsLoading(false);
            }
          } else {
            if (replace) {
              console.log('Replacing file in S3...');
              try {
                // Convert file to base64
                const base64File = await fileToBase64(file);
                
                const uploadResponse = await apiClient.queries.uploadFile({
                  file: base64File,
                  path: `files/${existingFile.id}`,
                  bucketName: S3_BUCKET_NAME,
                });

                console.log(
                  'File successfully replaced in S3 with ID:',
                  existingFile.id,
                );
                console.log('Upload response:', uploadResponse);

                await startStepFunction(existingFile.id, true);
                navigate(`/file-overview?fileId=${existingFile.id}`);
              } catch (uploadError) {
                console.error('Failed to replace file in S3:', uploadError);
                setIsLoading(false);
              }
            } else {
              setIsReplace(true);
              console.log(
                'File already exists. Waiting for user confirmation to replace.',
              );
              setIsLoading(false);
            }
          }
        } catch (error) {
          console.error('Error processing file:', error);
          setIsLoading(false);
        }
      } else {
        console.error('Failed to read file content.');
        setIsLoading(false);
      }
    };

    reader.onerror = (error) => {
      console.error('Error reading file:', error);
      setIsLoading(false);
    };

    reader.readAsText(file);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        uploadFileRef.current &&
        !uploadFileRef.current.contains(event.target as Node)
      ) {
        handleCloseUploadFile();
      }
    };

    if (isUploadFileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUploadFileOpen]);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        width: '100%',
        minHeight: '1110px',

        justifyContent: 'flex-end',
        boxSizing: 'border-box',
      }}
    >
      <img
        alt="coinsEmptyState"
        src="/assets/background/coinsEmptyState.svg"
        style={{
          position: 'absolute',
          top: 50,
          left: 0,
          width: '760px',
          height: '941px',

          zIndex: 1,
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '40px',
          width: '700px',
          alignItems: 'flex-end',
          zIndex: 2,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ cursor: 'pointer' }} onClick={handleCloseUploadFile}></Box>
          <Box sx={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            <Typography
              fontFamily={'Heebo'}
              fontWeight={'300'}
              fontSize={'40px'}
              dir="rtl"
            >
              בכדי להתחיל ליצור הוראות
              <br /> תשלום, יש להעלות קובץ XML
            </Typography>
            <img src="/assets/background/xml.svg" alt="Upload Icon" />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '318px',
            backgroundColor: '#FFFFFFB2',
            borderRadius: '15px',
            cursor: 'pointer',
          }}
          onClick={handleBoxClick}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              alignItems: 'center',
            }}
          >
            <img src="/assets/uploadFile/coins.svg" alt="Upload" />
            <Typography fontSize={'12px'} fontWeight={'700'}>
              לחץ כאן או גרור את הקובץ
            </Typography>
          </Box>
          <input
            type="file"
            accept=".dat"
            ref={inputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </Box>
        {file && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row-reverse',
              gap: '10px',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                backgroundColor: '#00000042',
                py: '5px',
                px: '15px',
                display: 'flex',
                gap: '5px',
                alignItems: 'center',
                borderRadius: '30px',
                width: '100%',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              <Typography
                fontWeight={'700'}
                fontSize={'14px'}
                fontFamily={'AlmoniDL'}
                color={'#FFF'}
              >
                {file.name}
              </Typography>
              <Icon
                style={{ cursor: 'pointer' }}
                icon={'material-symbols:close'}
                color="#FFF"
                onClick={() => setFile(null)}
              />
            </Box>
          </Box>
        )}
        {file && (
          <SendButton
            loading={isLoading}
            label="העלאת קובץ"
            light
            onClick={handleNextStep}
          />
        )}
      </Box>
    </Box>
  );
};
